import Button from 'components/button';
import RadioCardList from 'components/radioCardList/RadioCardList';
import SlideOverBody from 'components/SlideOver/component/body';
import SlideOverFooter from 'components/SlideOver/component/footer';
import FieldLabel from 'components/styledInput/FieldLabel';
import StyledInput from 'components/styledInput/StyledInput';
import useText from 'hooks/useText';
import { useEffect, useRef, useState } from 'react';

export const NOT_DONE_REASONS = [
  'no_show',
  'canceled_by_client',
  'canceled_by_us',
  'postponed',
  'other',
];
export const MAX_NOT_DONE_DETAIL = 500;

/**
 * Why an appointment was not held, in words: its reason and its note (what
 * was written for « Autre » stands alone); '' for an appointment marked
 * before reasons existed.
 */
export const notDoneReasonText = (reason, detail, text) => {
  const note = detail?.trim() ?? '';
  if (reason === 'other') return note || text('reason_other');
  if (!NOT_DONE_REASONS.includes(reason)) return '';
  return note ? `${text(`reason_${reason}`)} — ${note}` : text(`reason_${reason}`);
};

/**
 * « Non réalisé » of an appointment, in its slide-over in place of the
 * detail: its reason, one of NOT_DONE_REASONS, and a note on why, both
 * required. « Enregistrer » hands (reason, detail) to `onConfirm` and stays
 * on « Enregistrement… » until it settles; « Annuler » and Échap call
 * `onCancel`.
 */
const NotDoneForm = ({ onCancel, onConfirm }) => {
  const { text } = useText('appointment');
  const [reason, setReason] = useState(null);
  const [detail, setDetail] = useState('');
  const [error, setError] = useState('');
  const [pending, setPending] = useState(false);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  // As while editing: Échap goes back to the detail, the slide-over stays.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented) cancelRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const confirm = async () => {
    if (pending) return;
    if (!reason) return setError(text('notDoneReasonRequired'));
    const precision = detail.trim();
    if (!precision) return setError(text('notDoneDetailRequired'));
    setPending(true);
    try {
      await onConfirm(reason, precision);
    } finally {
      setPending(false);
    }
  };

  return (
    <>
      <SlideOverBody>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <FieldLabel label={text('notDoneReason')} />
            <RadioCardList
              name="appointment-not-done-reason"
              label={text('notDoneReason')}
              options={NOT_DONE_REASONS.map((value) => ({ value, label: text(`reason_${value}`) }))}
              value={reason}
              onChange={(value) => {
                setReason(value);
                setError('');
              }}
            />
          </div>
          <StyledInput
            type="textarea"
            label="Note"
            placeholder="Pourquoi il n’a pas eu lieu…"
            maxLength={MAX_NOT_DONE_DETAIL}
            value={detail}
            onChange={(event) => {
              setDetail(event.target.value);
              setError('');
            }}
          />
          {error && (
            <p role="alert" className="text-xs text-red-600">
              {error}
            </p>
          )}
        </div>
      </SlideOverBody>
      <div className="flex-shrink-0 border-t border-gray-100">
        <SlideOverFooter>
          <Button color="SECONDARY" size="SM" onClick={onCancel} disabled={pending}>
            {text('notDoneCancel')}
          </Button>
          <Button color="PRIMARY" size="SM" onClick={confirm} disabled={pending}>
            {text(pending ? 'notDoneConfirmPending' : 'notDoneConfirm')}
          </Button>
        </SlideOverFooter>
      </div>
    </>
  );
};

export default NotDoneForm;
